import { type ReactNode, useRef, useState } from "react";
import { useForgeEvent } from "./hooks";

export function Dialog({ open, onClose, label, persistent, preset, wide, children }: {
  open: boolean; onClose: () => void; label: string; persistent?: boolean; preset?: "right-sheet"; wide?: boolean; children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  useForgeEvent(ref, "forge-dialog-close", onClose);
  return (
    <forge-dialog ref={ref} open={open || undefined} label={label} persistent={persistent || undefined} preset={preset}>
      <div className={preset ? `sheet-body${wide ? " sheet-body--wide" : ""}` : `dialog-body${wide ? " dialog-body--wide" : ""}`}>{children}</div>
    </forge-dialog>
  );
}

export function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <forge-button variant="outlined" dense onClick={async () => {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }}>
      <forge-icon slot="start" name={copied ? "check_circle" : "content_copy"} />
      {copied ? "Copied" : label}
    </forge-button>
  );
}

export function TagChips({ division, businessUnit }: { division: string | null; businessUnit: string | null }) {
  if (!division) {
    return (
      <span className="row gap-xsmall" title="Spend on this key cannot be attributed to a division">
        <forge-icon name="warning" style={{ color: "var(--forge-theme-warning)" }} /> Unlabelled
      </span>
    );
  }
  return (
    <span className="row wrap gap-xsmall">
      <span className="tag-pill">{division}</span>
      {businessUnit && <span className="tag-pill">{businessUnit}</span>}
    </span>
  );
}

export function Loading() {
  return <forge-skeleton style={{ height: "120px" }} />;
}

export function ErrorMessage({ error }: { error: unknown }) {
  return (
    <forge-inline-message theme="error">
      <forge-icon slot="icon" name="error" />
      <div slot="title">Something went wrong</div>
      <span>{(error as Error)?.message ?? String(error)}</span>
    </forge-inline-message>
  );
}

/**
 * Compact empty state. Not forge-page-state: its graphic slot reserves an illustration-sized
 * area, so a small icon left the title and message below the fold and the page looked blank.
 */
export function Empty({ title, message, action, icon = "search" }: { title: string; message: string; action?: ReactNode; icon?: string }) {
  return (
    <div className="empty-state" role="status">
      <forge-icon name={icon} className="empty-state__icon" />
      <h2 className="forge-typography--heading3 card-title">{title}</h2>
      <p className="forge-typography--body2 muted">{message}</p>
      {action}
    </div>
  );
}
